import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import axiosApiInstance from "infrastructure/utils/api";
import storage from "infrastructure/utils/storage";
import {
  NOTIFICATIONS_BASE_API,
  NOTIFICATIONS_READ_ALL_API,
  NOTIFICATIONS_READ_BY_ID_API,
} from "../constants";
import { useNotificationsStore } from "../hooks/useNotificationsStore";
import { useNotificationsApiSync } from "../hooks/useNotificationsApiSync";
import { NOTIFICATION_DOMAIN_EVENT_NAMES, NotificationItem } from "../types";
import { buildNotificationsChannelName } from "../realtime/channel";
import {
  resolveNotificationChannelType,
  resolveNotificationRecipientId,
} from "../session";
import {
  createNotificationsEchoClient,
  isReverbBroadcastingConfigured,
  type NotificationsEchoInstance,
} from "../realtime/echo";

type NotificationsContextValue = {
  notifications: NotificationItem[];
  unreadCount: number;
  addNotification: (envelope: unknown, wireEventName?: string) => void;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  hydrateFromApi: (items: NotificationItem[]) => void;
  refreshNotifications: () => Promise<unknown>;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export const NotificationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    notifications,
    unreadCount,
    addNotification,
    markAsRead: markAsReadLocal,
    markAllAsRead: markAllAsReadLocal,
    hydrateFromApi,
  } = useNotificationsStore();

  const isAuthed = Boolean(storage.getItem("access_token"));
  const recipientId = resolveNotificationRecipientId();
  const channelType = resolveNotificationChannelType();

  const { refreshNotifications } = useNotificationsApiSync({
    enabled: isAuthed,
    hydrateFromApi,
  });

  const refreshNotificationsRef = useRef(refreshNotifications);
  refreshNotificationsRef.current = refreshNotifications;

  const triggerRefreshNotifications = useCallback(() => refreshNotificationsRef.current(), []);

  useEffect(() => {
    if (!isAuthed) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void triggerRefreshNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthed, triggerRefreshNotifications]);

  const markAsRead = useCallback(
    async (id: string) => {
      markAsReadLocal(id);
      try {
        await axiosApiInstance.post(NOTIFICATIONS_READ_BY_ID_API(id));
      } catch (_error) {
        // noop by design; local optimistic state already updated
      } finally {
        await triggerRefreshNotifications();
      }
    },
    [markAsReadLocal, triggerRefreshNotifications]
  );

  const markAllAsRead = useCallback(async () => {
    markAllAsReadLocal();
    try {
      await axiosApiInstance.post(NOTIFICATIONS_READ_ALL_API);
    } catch (_error) {
      // noop by design; local optimistic state already updated
    } finally {
      await triggerRefreshNotifications();
    }
  }, [markAllAsReadLocal, triggerRefreshNotifications]);

  const echoRef = useRef<NotificationsEchoInstance | null>(null);
  const channelNameRef = useRef<string | null>(null);
  const reverbMissingConfigWarnedRef = useRef(false);

  useEffect(() => {
    if (!isAuthed || !recipientId) {
      if (echoRef.current && channelNameRef.current) {
        echoRef.current.leave(channelNameRef.current);
        echoRef.current.disconnect();
      }
      echoRef.current = null;
      channelNameRef.current = null;
      return;
    }

    if (!isReverbBroadcastingConfigured()) {
      if (
        process.env.NODE_ENV === "development" &&
        !reverbMissingConfigWarnedRef.current
      ) {
        reverbMissingConfigWarnedRef.current = true;
        // eslint-disable-next-line no-console
        console.warn(
          "[notifications] Realtime disabled: set REACT_APP_REVERB_APP_KEY (and related Reverb env vars) to enable websocket notifications."
        );
      }
      return;
    }

    if (!echoRef.current) {
      const client = createNotificationsEchoClient({
        authEndpoint: `${process.env.REACT_APP_BASE_API_URL}/${NOTIFICATIONS_BASE_API}/broadcasting/auth`,
      });
      if (!client) {
        return;
      }
      echoRef.current = client;
    }

    const channelName = buildNotificationsChannelName(channelType, recipientId);
    channelNameRef.current = channelName;
    const privateChannel = echoRef.current.private(channelName);

    NOTIFICATION_DOMAIN_EVENT_NAMES.forEach((eventName) => {
      privateChannel.listen(`.${eventName}`, (payload: unknown) => {
        addNotification(payload, eventName);
        void triggerRefreshNotifications();
      });
    });

    return () => {
      if (echoRef.current && channelNameRef.current) {
        echoRef.current.leave(channelNameRef.current);
      }
    };
  }, [addNotification, channelType, isAuthed, recipientId, triggerRefreshNotifications]);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      unreadCount,
      addNotification,
      hydrateFromApi,
      refreshNotifications: triggerRefreshNotifications,
      markAsRead,
      markAllAsRead,
    }),
    [
      addNotification,
      hydrateFromApi,
      markAllAsRead,
      markAsRead,
      notifications,
      triggerRefreshNotifications,
      unreadCount,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotifications must be used inside NotificationsProvider");
  }
  return context;
};
